//----------------------------------------------------
// AdminDashboard.jsx — Logboek24 Beheer Dashboard
// Alleen PlatformAdmin
//----------------------------------------------------

import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { Link } from "react-router-dom";

import Win10AdminLayout from "../layouts/Win10AdminLayout";
import Win10Modal from "../components/Win10Modal";
import { normalizeRole } from "../utils/roleMapper";
import { useUser } from "../context/UserContext";
import { API_BASE_URL } from "../config/api";

const API_URL = API_BASE_URL;

export default function AdminDashboard({ section = "dashboard" }) {
  const { login } = useUser();

  // -------------------------------
  // State
  // -------------------------------
  const [userInfo, setUserInfo] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [apiErrorsLast24h, setApiErrorsLast24h] = useState(0);
  const [apiErrorsLoading, setApiErrorsLoading] = useState(true);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [logoutOtherSessions, setLogoutOtherSessions] = useState(true);
  const [mfaStatusLoading, setMfaStatusLoading] = useState(true);
  const [mfaStatus, setMfaStatus] = useState({
    enabled: false,
    pendingSetup: false,
    backupCodesRemaining: 0,
  });
  const [mfaCurrentPassword, setMfaCurrentPassword] = useState("");
  const [mfaSetupLoading, setMfaSetupLoading] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaVerifyCode, setMfaVerifyCode] = useState("");
  const [mfaVerifyLoading, setMfaVerifyLoading] = useState(false);
  const [mfaDisableCode, setMfaDisableCode] = useState("");
  const [mfaDisableLoading, setMfaDisableLoading] = useState(false);
  const [mfaRegenCode, setMfaRegenCode] = useState("");
  const [mfaRegenLoading, setMfaRegenLoading] = useState(false);
  const [freshBackupCodes, setFreshBackupCodes] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({
    type: "info",
    title: "",
    message: "",
  });

  // -------------------------------
  // Auth check
  // -------------------------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/";
      return;
    }

    try {
      const decoded = jwtDecode(token);
      setUserInfo(decoded);
    } catch (err) {
      localStorage.removeItem("token");
      window.location.href = "/";
    }
  }, []);

  const normalizedRole = userInfo ? normalizeRole(userInfo.rol) : null;
  const showDashboard = section === "dashboard";
  const showSecurity = section === "security";
  const showOperations = section === "operations";
  const showIntegrations = section === "integrations";
  const title = showSecurity ? "Security & incidenten" : showOperations ? "Platform monitoring" : showIntegrations ? "Platform koppelingen" : "Logboek24 Beheer Dashboard";

  // -------------------------------
  // Helpers
  // -------------------------------
  const showModal = (type, title, message) => {
    setModalData({ type, title, message });
    setModalOpen(true);
  };

  async function forcePasswordResetAllUsers() {
    if (resetLoading) return;
    if (resetConfirmText.trim().toUpperCase() !== "RESET") {
      showModal(
        "warning",
        "Bevestiging ontbreekt",
        "Typ eerst RESET om een verplichte wachtwoordreset voor alle gebruikers te starten."
      );
      return;
    }

    try {
      setResetLoading(true);
      const res = await fetch(`${API_URL}/api/auth/admin/force-password-reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          scope: "all",
          reason: "platform_admin_emergency_reset",
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || "Bulk reset mislukt");
      }

      setResetConfirmText("");
      showModal(
        "info",
        "Password reset gestart",
        `Bijgewerkte gebruikers: ${data.updatedUsers || 0}. Ingetrokken sessies: ${data.revokedSessions || 0}.`
      );
    } catch (err) {
      showModal(
        "error",
        "Bulk reset mislukt",
        err.message || "De verplichte wachtwoordreset kon niet worden gestart."
      );
    } finally {
      setResetLoading(false);
    }
  }

  async function changeOwnPassword() {
    if (changePasswordLoading) return;

    const current = String(currentPassword || "").trim();
    const next = String(newPassword || "").trim();
    const confirm = String(confirmNewPassword || "").trim();

    if (!current || !next || !confirm) {
      showModal("warning", "Onvolledige invoer", "Vul alle wachtwoordvelden in.");
      return;
    }

    if (next.length < 10) {
      showModal(
        "warning",
        "Nieuw wachtwoord te kort",
        "Gebruik minimaal 10 tekens voor het nieuwe wachtwoord."
      );
      return;
    }

    if (next !== confirm) {
      showModal("warning", "Mismatch", "Nieuw wachtwoord en bevestiging zijn niet gelijk.");
      return;
    }

    if (next === current) {
      showModal(
        "warning",
        "Geen wijziging",
        "Nieuw wachtwoord moet verschillen van je huidige wachtwoord."
      );
      return;
    }

    try {
      setChangePasswordLoading(true);
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          currentPassword: current,
          newPassword: next,
          logoutOtherSessions,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || "Wachtwoord wijzigen mislukt");
      }

      if (data?.accessToken) {
        login(data.accessToken, String(data?.refreshToken || "").trim());
      }

      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");

      showModal(
        "info",
        "Wachtwoord bijgewerkt",
        `Je wachtwoord is gewijzigd. Ingetrokken sessies: ${Number(data?.revokedSessions || 0)}.`
      );
    } catch (err) {
      showModal(
        "error",
        "Wachtwoord wijzigen mislukt",
        err.message || "Probeer opnieuw."
      );
    } finally {
      setChangePasswordLoading(false);
    }
  }

  async function fetchMfaStatus() {
    try {
      setMfaStatusLoading(true);
      const res = await fetch(`${API_URL}/api/auth/mfa/status`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "MFA status ophalen mislukt");
      setMfaStatus({
        enabled: Boolean(data?.enabled),
        pendingSetup: Boolean(data?.pendingSetup),
        backupCodesRemaining: Number(data?.backupCodesRemaining || 0),
      });
    } catch (err) {
      setMfaStatus({
        enabled: false,
        pendingSetup: false,
        backupCodesRemaining: 0,
      });
    } finally {
      setMfaStatusLoading(false);
    }
  }

  async function startMfaSetup() {
    const pwd = String(mfaCurrentPassword || "").trim();
    if (!pwd) {
      showModal("warning", "Wachtwoord ontbreekt", "Vul eerst je huidige wachtwoord in.");
      return;
    }
    try {
      setMfaSetupLoading(true);
      const res = await fetch(`${API_URL}/api/auth/mfa/setup/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ currentPassword: pwd }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "MFA setup starten mislukt");
      setMfaSetupData(data);
      setFreshBackupCodes([]);
      setMfaVerifyCode("");
      showModal("info", "MFA setup gestart", "Scan de QR met Microsoft Authenticator en verifieer met een code.");
      await fetchMfaStatus();
    } catch (err) {
      showModal("error", "MFA setup mislukt", err.message || "Probeer opnieuw.");
    } finally {
      setMfaSetupLoading(false);
    }
  }

  async function verifyMfaSetup() {
    const code = String(mfaVerifyCode || "").trim();
    if (!code) {
      showModal("warning", "Code ontbreekt", "Vul de verificatiecode uit je authenticator in.");
      return;
    }
    try {
      setMfaVerifyLoading(true);
      const res = await fetch(`${API_URL}/api/auth/mfa/setup/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "MFA verifiëren mislukt");
      setFreshBackupCodes(Array.isArray(data?.backupCodes) ? data.backupCodes : []);
      setMfaSetupData(null);
      setMfaVerifyCode("");
      showModal("info", "MFA ingeschakeld", "Bewaar de getoonde backup codes veilig.");
      await fetchMfaStatus();
    } catch (err) {
      showModal("error", "MFA verificatie mislukt", err.message || "Probeer opnieuw.");
    } finally {
      setMfaVerifyLoading(false);
    }
  }

  async function disableMfa() {
    const pwd = String(mfaCurrentPassword || "").trim();
    const code = String(mfaDisableCode || "").trim();
    if (!pwd || !code) {
      showModal("warning", "Gegevens ontbreken", "Vul je wachtwoord en MFA code in.");
      return;
    }
    try {
      setMfaDisableLoading(true);
      const res = await fetch(`${API_URL}/api/auth/mfa/disable`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ currentPassword: pwd, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "MFA uitschakelen mislukt");
      setMfaDisableCode("");
      setMfaSetupData(null);
      setFreshBackupCodes([]);
      showModal("info", "MFA uitgeschakeld", "MFA is uitgeschakeld voor dit account.");
      await fetchMfaStatus();
    } catch (err) {
      showModal("error", "MFA uitschakelen mislukt", err.message || "Probeer opnieuw.");
    } finally {
      setMfaDisableLoading(false);
    }
  }

  async function regenerateBackupCodes() {
    const pwd = String(mfaCurrentPassword || "").trim();
    const code = String(mfaRegenCode || "").trim();
    if (!pwd || !code) {
      showModal("warning", "Gegevens ontbreken", "Vul je wachtwoord en MFA code in.");
      return;
    }
    try {
      setMfaRegenLoading(true);
      const res = await fetch(`${API_URL}/api/auth/mfa/backup-codes/regenerate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ currentPassword: pwd, code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Backup codes vernieuwen mislukt");
      setFreshBackupCodes(Array.isArray(data?.backupCodes) ? data.backupCodes : []);
      setMfaRegenCode("");
      showModal("info", "Backup codes vernieuwd", "Nieuwe backup codes zijn gegenereerd.");
      await fetchMfaStatus();
    } catch (err) {
      showModal("error", "Backup codes vernieuwen mislukt", err.message || "Probeer opnieuw.");
    } finally {
      setMfaRegenLoading(false);
    }
  }

  // -------------------------------
  // Moneybird tokens
  // -------------------------------
  useEffect(() => {
    if (!userInfo) return;

    const fetchTokens = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/moneybird/admin/tokens`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const data = await res.json();

        if (res.ok) {
          setTokens(data.data || []);
        } else {
          setError("Fout bij ophalen tokens");
        }
      } catch (err) {
        setError("Backend niet bereikbaar");
        showModal(
          "error",
          "Backend fout",
          "De backend reageert niet."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTokens();
  }, [userInfo]);

  useEffect(() => {
    if (!userInfo) return;
    fetchMfaStatus();
  }, [userInfo]);

  useEffect(() => {
    if (!userInfo) return;

    let cancelled = false;

    async function fetchApiErrorSummary() {
      setApiErrorsLoading(true);
      try {
        const res = await fetch(
          `${API_URL}/api/auditlogs?action=api_error&status=new&limit=500`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const data = await res.json().catch(() => []);
        if (!res.ok) throw new Error("Kon API-fouten niet ophalen");

        const list = Array.isArray(data) ? data : [];
        const cutoff = Date.now() - 24 * 60 * 60 * 1000;
        const count = list.filter((row) => {
          const ts = new Date(row?.createdAt).getTime();
          return Number.isFinite(ts) && ts >= cutoff;
        }).length;

        if (!cancelled) setApiErrorsLast24h(count);
      } catch (_err) {
        if (!cancelled) setApiErrorsLast24h(0);
      } finally {
        if (!cancelled) setApiErrorsLoading(false);
      }
    }

    fetchApiErrorSummary();
    return () => {
      cancelled = true;
    };
  }, [userInfo]);

  if (userInfo && normalizedRole !== "admin") {
    return (
      <Win10AdminLayout>
        <h1 className="text-xl font-bold text-red-600 mb-2">
          🚫 Geen toegang
        </h1>
        <p className="text-gray-600">
          Dit dashboard is alleen toegankelijk voor platformbeheerders.
        </p>
      </Win10AdminLayout>
    );
  }

  // -------------------------------
  // Render
  // -------------------------------
  return (
    <Win10AdminLayout>
      <Win10Modal
        open={modalOpen}
        type={modalData.type}
        title={modalData.title}
        message={modalData.message}
        onClose={() => setModalOpen(false)}
        onConfirm={() => setModalOpen(false)}
      />

      <h1 className="text-2xl font-bold mb-6">⚙️ {title}</h1>

      {showDashboard ? <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Link to="/admin/tenants" className="rounded-md border border-gray-300 bg-white p-5 shadow-sm hover:bg-blue-50"><h2 className="font-semibold">Tenants</h2><p className="mt-1 text-sm text-gray-600">Beheer tenantstatus, gebruikers, modules en onboarding.</p></Link>
        <Link to="/admin/operations" className="rounded-md border border-gray-300 bg-white p-5 shadow-sm hover:bg-blue-50"><h2 className="font-semibold">Platform monitoring</h2><p className="mt-1 text-sm text-gray-600">API-fouten en operationele signalen.</p></Link>
        <Link to="/admin/security" className="rounded-md border border-gray-300 bg-white p-5 shadow-sm hover:bg-blue-50"><h2 className="font-semibold">Security & incidenten</h2><p className="mt-1 text-sm text-gray-600">MFA, eigen wachtwoord en noodacties.</p></Link>
      </div> : null}

      {showIntegrations ? <div className="bg-white shadow-md rounded-md p-6 border border-gray-300">
        <h2 className="text-xl font-semibold mb-4">
          💰 Moneybird Koppelingen
        </h2>

        {loading ? (
          <p className="text-gray-500">Laden...</p>
        ) : error ? (
          <p className="text-red-600">{error}</p>
        ) : tokens.length === 0 ? (
          <p className="text-gray-600">Geen gekoppelde tenants.</p>
        ) : (
          <ul className="space-y-2">
            {tokens.map((t) => (
              <li
                key={t.tenantId}
                className="p-3 border rounded bg-gray-50"
              >
                <strong>{t.tenantId}</strong> —{" "}
                {t.verlopen ? "Verlopen" : "Geldig"}
              </li>
            ))}
          </ul>
        )}
      </div> : null}

      {showSecurity ? <><div className="mt-6 bg-white shadow-md rounded-md p-6 border border-amber-300">
        <h2 className="text-xl font-semibold mb-2">🛡️ Multi-factor authenticatie</h2>
        <p className="text-sm text-gray-700 mb-3">
          Microsoft Authenticator (TOTP) voor dit Platform Admin account.
        </p>
        <div className="mb-4 text-sm text-gray-800">
          {mfaStatusLoading
            ? "MFA status laden..."
            : `Status: ${mfaStatus.enabled ? "Ingeschakeld" : "Uitgeschakeld"} • Backup codes over: ${mfaStatus.backupCodesRemaining}`}
        </div>

        <div className="grid gap-3 max-w-md">
          <input
            type="password"
            value={mfaCurrentPassword}
            onChange={(event) => setMfaCurrentPassword(event.target.value)}
            placeholder="Huidig wachtwoord (vereist voor MFA-acties)"
            className="border border-gray-300 rounded-md px-3 py-2"
            autoComplete="current-password"
          />
        </div>

        {!mfaStatus.enabled && (
          <div className="mt-4 max-w-md">
            <button
              type="button"
              onClick={startMfaSetup}
              disabled={mfaSetupLoading}
              className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-60"
            >
              {mfaSetupLoading ? "Setup starten..." : "Start MFA setup"}
            </button>
          </div>
        )}

        {mfaSetupData && (
          <div className="mt-4 p-4 border rounded-md bg-gray-50 max-w-xl">
            <p className="text-sm text-gray-700 mb-2">1. Scan de QR-code met Microsoft Authenticator.</p>
            {mfaSetupData?.qrCodeDataUrl && (
              <img src={mfaSetupData.qrCodeDataUrl} alt="MFA QR" className="w-44 h-44 border bg-white p-1" />
            )}
            <p className="text-xs text-gray-700 mt-2 break-all">
              Handmatige sleutel: <span className="font-mono">{mfaSetupData?.secret || mfaSetupData?.secretMasked}</span>
            </p>
            <div className="mt-3 flex flex-col gap-2">
              <input
                type="text"
                value={mfaVerifyCode}
                onChange={(event) => setMfaVerifyCode(event.target.value)}
                placeholder="2. Voer 6-cijferige code in"
                className="border border-gray-300 rounded-md px-3 py-2"
              />
              <button
                type="button"
                onClick={verifyMfaSetup}
                disabled={mfaVerifyLoading}
                className="bg-green-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-60"
              >
                {mfaVerifyLoading ? "Verifiëren..." : "Bevestig en activeer MFA"}
              </button>
            </div>
          </div>
        )}

        {mfaStatus.enabled && (
          <div className="mt-4 grid gap-4 max-w-md">
            <div className="grid gap-2">
              <input
                type="text"
                value={mfaDisableCode}
                onChange={(event) => setMfaDisableCode(event.target.value)}
                placeholder="MFA code om uit te schakelen"
                className="border border-gray-300 rounded-md px-3 py-2"
              />
              <button
                type="button"
                onClick={disableMfa}
                disabled={mfaDisableLoading}
                className="bg-red-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-60"
              >
                {mfaDisableLoading ? "Bezig..." : "Schakel MFA uit"}
              </button>
            </div>
            <div className="grid gap-2">
              <input
                type="text"
                value={mfaRegenCode}
                onChange={(event) => setMfaRegenCode(event.target.value)}
                placeholder="MFA code om backup codes te vernieuwen"
                className="border border-gray-300 rounded-md px-3 py-2"
              />
              <button
                type="button"
                onClick={regenerateBackupCodes}
                disabled={mfaRegenLoading}
                className="bg-amber-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-amber-700 disabled:opacity-60"
              >
                {mfaRegenLoading ? "Bezig..." : "Genereer nieuwe backup codes"}
              </button>
            </div>
          </div>
        )}

        {freshBackupCodes.length > 0 && (
          <div className="mt-4 p-4 border rounded-md bg-yellow-50 border-yellow-300 max-w-md">
            <p className="text-sm font-semibold text-yellow-900 mb-2">Backup codes (eenmalig tonen)</p>
            <ul className="space-y-1 font-mono text-sm text-yellow-900">
              {freshBackupCodes.map((code) => (
                <li key={code}>{code}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-6 bg-white shadow-md rounded-md p-6 border border-amber-300">
        <h2 className="text-xl font-semibold mb-2">
          🔑 Eigen wachtwoord wijzigen
        </h2>
        <p className="text-sm text-gray-700 mb-4">
          Wijzig je Platform Admin wachtwoord direct vanuit de UI.
        </p>
        <div className="grid gap-3 max-w-md">
          <input
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            placeholder="Huidig wachtwoord"
            className="border border-gray-300 rounded-md px-3 py-2"
            autoComplete="current-password"
          />
          <input
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            placeholder="Nieuw wachtwoord (minimaal 10 tekens)"
            className="border border-gray-300 rounded-md px-3 py-2"
            autoComplete="new-password"
          />
          <input
            type="password"
            value={confirmNewPassword}
            onChange={(event) => setConfirmNewPassword(event.target.value)}
            placeholder="Bevestig nieuw wachtwoord"
            className="border border-gray-300 rounded-md px-3 py-2"
            autoComplete="new-password"
          />
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={logoutOtherSessions}
              onChange={(event) => setLogoutOtherSessions(event.target.checked)}
            />
            Andere actieve sessies direct uitloggen
          </label>
          <button
            type="button"
            onClick={changeOwnPassword}
            disabled={changePasswordLoading}
            className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-60"
          >
            {changePasswordLoading ? "Bezig..." : "Wijzig mijn wachtwoord"}
          </button>
        </div>
      </div>

      <div className="mt-6 bg-white shadow-md rounded-md p-6 border border-amber-300">
        <h2 className="text-xl font-semibold mb-2">
          🔐 Noodmaatregel wachtwoorden
        </h2>
        <p className="text-sm text-gray-700 mb-4">
          Hiermee markeer je alle accounts voor een verplichte wachtwoordwijziging
          en trek je actieve sessies in. Gebruik dit alleen bij een echt
          security-incident.
        </p>

        <div className="flex flex-col gap-3 max-w-md">
          <input
            type="text"
            value={resetConfirmText}
            onChange={(event) => setResetConfirmText(event.target.value)}
            placeholder="Typ RESET om te bevestigen"
            className="border border-gray-300 rounded-md px-3 py-2"
          />
          <button
            type="button"
            onClick={forcePasswordResetAllUsers}
            disabled={resetLoading}
            className="bg-red-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-60"
          >
            {resetLoading
              ? "Bezig met resetten..."
              : "Forceer wachtwoordreset voor alle gebruikers"}
          </button>
        </div>
      </div></> : null}

      {showOperations ? <div className="mt-6 bg-white shadow-md rounded-md p-6 border border-gray-300">
        <h2 className="text-xl font-semibold mb-2">📋 Foutmonitoring</h2>
        <p className="text-sm text-gray-700 mb-4">
          Bekijk recente API-fouten (4xx/5xx) inclusief route, foutcode en veldfouten.
        </p>
        <div className="mb-4 inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
          {apiErrorsLoading
            ? "Nieuwe API-fouten (24u): laden..."
            : `Nieuwe API-fouten (24u): ${apiErrorsLast24h}`}
        </div>
        <div>
          <Link
            to="/admin/api-fouten?status=new"
            className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            Open nieuwe API-fouten
          </Link>
        </div>
        <div className="mt-3">
          <Link
            to="/admin/api-fouten"
            className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Open volledig API fouten overzicht
          </Link>
        </div>
      </div> : null}
    </Win10AdminLayout>
  );
}
